// src/pages/RegisterPage.js
// Updated: 11/01/2025 09:27 PM EDT
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; 
import { auth, db } from '../firebaseConfig'; 
import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  deleteUser, 
} from 'firebase/auth';
import { doc, writeBatch, getDoc, serverTimestamp, increment } from 'firebase/firestore'; 

import AuthLayout from '../components/AuthLayout'; 
import { Eye, EyeOff } from 'lucide-react'; // Import icons for visibility toggle

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState(''); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // --- STATE FOR CONFIRMATION & VISIBILITY ---
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // --- END NEW STATE ---
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [isJoiningOrg, setIsJoiningOrg] = useState(false);
  const [targetOrgId, setTargetOrgId] = useState(null);
  const [orgInviteCode, setOrgInviteCode] = useState(null);
  const [targetInviteId, setTargetInviteId] = useState(null); 

  const navigate = useNavigate();
  const location = useLocation();

  // --- REMOVED REDUNDANT showJoinForm STATE ---
  const [manualJoinCode, setManualJoinCode] = useState('');
  const [joinCodeError, setJoinCodeError] = useState('');
  const [isCheckingCode, setIsCheckingCode] = useState(false);

  // --- NEW STATE: Check if we should default to join flow ---
  const [isJoinFlowActive, setIsJoinFlowActive] = useState(false);

  const validateJoinCode = async (code) => {
    if (!code) return;
    
    setIsCheckingCode(true);
    setJoinCodeError('');
    setError(''); 
    
    try {
      const inviteDocRef = doc(db, 'invitationCodes', code);
      const inviteSnapshot = await getDoc(inviteDocRef);
      
      if (inviteSnapshot.exists()) {
        const inviteData = inviteSnapshot.data();
        const fetchedOrgId = inviteData.orgId;
        const fetchedInviteId = inviteData.inviteId; 

        // --- CHECK IF INVITATION IS USED ---
        const invitationDocRef = doc(db, 'invitations', fetchedInviteId);
        const invitationSnap = await getDoc(invitationDocRef);
        if (!invitationSnap.exists()) {
          throw new Error('Invalid invitation details.');
        }
        const invitationData = invitationSnap.data();
        if (invitationData.usageCount >= invitationData.maxUses) {
          throw new Error('This invitation code has already been used.');
        }
        // --- END OF CHECK ---

        const orgDocRef = doc(db, 'organizations', fetchedOrgId);
        const orgSnap = await getDoc(orgDocRef);

        if (orgSnap.exists()) {
          setOrgName(orgSnap.data().name);
          setTargetOrgId(fetchedOrgId);
          setTargetInviteId(fetchedInviteId); 
          setIsJoiningOrg(true);
          setOrgInviteCode(code); 
          // Since it's valid, we keep the flow active but registration fields appear below
          setIsJoinFlowActive(true); 
        } else {
           throw new Error('Organization not found for this code.');
        }
      } else {
        throw new Error('Invalid or expired join code.');
      }
    } catch(e) {
        console.error("Error validating join code:", e);
        setJoinCodeError(e.message || "Error validating join code.");
        setIsJoiningOrg(false);
        setTargetOrgId(null);
        setOrgName('');
    } finally {
      setIsCheckingCode(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeFromUrl = queryParams.get('join');
    const flowFromUrl = queryParams.get('flow'); 
    
    if (flowFromUrl === 'join') {
        setIsJoinFlowActive(true);
    } else if (!isJoiningOrg) {
        // If we are definitely creating a new org, explicitly set to false
        setIsJoinFlowActive(false);
    }

    if (codeFromUrl) {
      validateJoinCode(codeFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleValidateCode = (e) => {
    e.preventDefault();
    validateJoinCode(manualJoinCode.trim().toUpperCase());
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); 
    setJoinCodeError('');
    setIsLoading(true);

    if (name.trim() === '') { setError('Please fill out all fields.'); setIsLoading(false); return; }
    
    // --- ADD PASSWORD CONFIRMATION CHECK ---
    if (password !== confirmPassword) {
        setError('Passwords do not match.');
        setIsLoading(false);
        return;
    }
    // --- END CHECK ---

    let user;

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      user = userCredential.user;

      await updateProfile(user, {
        displayName: name,
      });

      await sendEmailVerification(user);

    } catch (authError) {
        if (authError.code === 'auth/email-already-in-use') { setError('This email address is already in use.'); } 
        else if (authError.code === 'auth/weak-password') { setError('Password should be at least 6 characters.'); } 
        else { setError('Failed to create an account. Please try again.'); console.error('Registration Auth Error:', authError); }
        setIsLoading(false);
        return;
    }

    try {
      const batch = writeBatch(db);
      let finalOrgId = targetOrgId;

      if (!isJoiningOrg) {
        // Case A: Creating a NEW Organization
        if (orgName.trim() === '') {
             setError('Organization name is required.');
             setIsLoading(false);
             await deleteUser(user);
             return; 
        }
        const orgRef = doc(db, 'organizations', crypto.randomUUID());
        finalOrgId = orgRef.id;

        batch.set(orgRef, {
          name: orgName,
          ownerId: user.uid,
          createdAt: serverTimestamp(),
        });
      } else if (isJoiningOrg && targetInviteId) {
        // Case B: Joining an org, so increment the usage count
        const invitationRef = doc(db, 'invitations', targetInviteId);
        batch.update(invitationRef, {
          usageCount: increment(1)
        });
      }
      
      const userRef = doc(db, 'users', user.uid);
      batch.set(userRef, {
        uid: user.uid,
        displayName: name,
        email: email,
        orgId: finalOrgId,
        role: isJoiningOrg ? 'member' : 'admin', 
        createdAt: serverTimestamp(),
        emailVerified: false,
      });

      await batch.commit();

      navigate('/dashboard');

    } catch (dbError) {
        console.error('Registration DB Write Error:', dbError);
        setError('Account created, but failed to link to organization. Please log in and contact support.');
        
        try {
            await deleteUser(user);
            console.log('Successfully deleted partially created Auth user.');
        } catch (cleanupError) {
            console.error('Failed to clean up Auth user:', cleanupError);
            setError('Account partially created. Please clear browser data and try again, or contact support.');
        }
        
        setIsLoading(false);
    }
  };
  
  // Conditionally set the title based on the flow
  let pageTitle = "Create your new account";
  if (isJoiningOrg) {
    pageTitle = `Join ${orgName || 'Organization'}`;
  } else if (isJoinFlowActive) {
    pageTitle = "Join an Organization";
  }

  return (
    <AuthLayout title={pageTitle}>
      <form className="space-y-4" onSubmit={handleRegister}>
        
        {isJoiningOrg && targetOrgId && (
            <div className="p-3 bg-green-100 border border-green-300 rounded-md text-sm text-green-800">
                You are registering to join **{orgName}** with code **{orgInviteCode}**.
            </div>
        )}
        {isCheckingCode && !isJoiningOrg && (
             <div className="p-3 bg-yellow-100 border border-yellow-300 rounded-md text-sm text-yellow-800">
                Verifying join code...
            </div>
        )}

        {/* --- MODIFIED LOGIC FOR FORM DISPLAY --- */}
        {!isJoiningOrg && isJoinFlowActive ? (
          // Flow A: Prompting for code or failed validation (requires user to input code)
            <div className="space-y-3">
                <div className="rounded-md border border-gray-300 bg-gray-50 p-4">
                  <label htmlFor="joinCode" className="block text-sm font-medium text-gray-700">
                    Enter Join Code to Continue
                  </label>
                  <div className="flex space-x-2">
                    <input
                      id="joinCode"
                      name="joinCode"
                      type="text"
                      value={manualJoinCode}
                      onChange={(e) => setManualJoinCode(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="ABC123"
                    />
                    <button
                      type="button"
                      onClick={handleValidateCode}
                      disabled={isCheckingCode || manualJoinCode.length < 1}
                      className="flex-shrink-0 rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isCheckingCode ? '...' : 'Verify'}
                    </button>
                  </div>
                  {joinCodeError && <p className="mt-2 text-sm text-red-600">{joinCodeError}</p>}
                </div>
                 <div className="text-center text-sm">
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                        Need to create an organization instead?
                    </Link>
                </div>
            </div>
        ) : !isJoiningOrg && !isJoinFlowActive ? (
          // Flow B: Standard "Create Organization" view
           <div>
                <label htmlFor="orgName" className="block text-sm font-medium text-gray-700">
                  Organization Name (You will be the Admin)
                </label>
                <input
                  id="orgName"
                  name="orgName"
                  type="text"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                 <div className="mt-4 text-center text-sm">
                    <Link to="/register?flow=join" className="font-medium text-blue-600 hover:text-blue-500">
                        Already have a join code?
                    </Link>
                </div>
            </div>
        ) : (
             // Flow C: Code validated (or pre-filled), proceed to registration
             <input type="hidden" value={orgName} /> 
        )}
        {/* --- END MODIFIED LOGIC --- */}
        
        {/* Only show the rest of the form if the organization details are set (either created or validated) */}
        {(isJoiningOrg || !isJoinFlowActive) && (
            <>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                <input id="name" name="name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
              </div>

              {/* --- MODIFIED PASSWORD FIELD --- */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <div className="relative mt-1">
                    <input 
                      id="password" 
                      name="password" 
                      type={showPassword ? 'text' : 'password'} 
                      autoComplete="new-password" 
                      required 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      className="block w-full pr-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                </div>
              </div>

              {/* --- NEW CONFIRM PASSWORD FIELD --- */}
               <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirm Password</label>
                <div className="relative mt-1">
                    <input 
                      id="confirmPassword" 
                      name="confirmPassword" 
                      type={showPassword ? 'text' : 'password'} 
                      autoComplete="new-password" 
                      required 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)} 
                      className="block w-full pr-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                    />
                    {/* Re-use visibility toggle on confirmation field */}
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                </div>
              </div>
              {/* --- END NEW FIELDS --- */}

              {error && <div className="text-sm text-red-600">{error}</div>}

              <div>
                <button
                  type="submit"
                  disabled={isLoading || isCheckingCode || (isJoiningOrg && !targetOrgId)} 
                  className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {isLoading ? 'Processing...' : 'Create Account'}
                </button>
              </div>

              <div className="mt-4 text-center text-xs text-gray-500">
                <p>
                  By creating an account, you agree to our
                  <Link to="/terms" className="mx-1 font-medium text-blue-600 hover:text-blue-500" target="_blank" rel="noopener noreferrer">
                    Terms of Conditions
                  </Link>
                  and
                  <Link to="/privacy" className="ml-1 font-medium text-blue-600 hover:text-blue-500" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </>
        )}
      </form>

      <div className="mt-6 text-center text-sm">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
          Sign in
        </Link>
      </div>
    </AuthLayout>
  );
}
